import { Waveform, CheckCircle, Phone, ArrowLeft, Tag, X, WarningCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { trackEvent, Events } from "@/lib/analytics";

import { API } from "@/lib/apiBase";

// Default INR fallback for the brief moment before /geo/pricing-context resolves.
const PLAN_FEATURES = ["2500 credits every month", "Up to 83 generations (166 songs)", "2 songs per generation · 30 credits", "Models: V6, V6 Wild, V6 Mini", "Unlimited downloads", "Song history access", "30-day validity"];
const DEFAULT_PLANS = [{ id: "monthly", name: "Monthly", inr: 899, credits: 2500, popular: true, features: PLAN_FEATURES }];

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [plans, setPlans] = useState(DEFAULT_PLANS);

  useEffect(() => {
    // Track pricing page view in GA4 (top of purchase funnel)
    trackEvent(Events.PRICING_VIEWED);
    axios.get(`${API}/geo/pricing-context`).then((res) => {
      const apiPlans = res.data.plans;
      if (apiPlans?.length) setPlans(apiPlans.map((p) => ({ ...p, popular: true, features: PLAN_FEATURES })));
    }).catch(() => { /* keep fallback */ });
  }, []);

  const formatPrice = (plan) => `₹${plan.inr}`;

  // Returns {hasDiscount, original, discounted, savings} all as formatted strings + raw numbers.
  const getPriceBreakdown = (plan) => {
    if (!appliedCoupon) {
      return { hasDiscount: false, original: formatPrice(plan), discounted: formatPrice(plan), savings: "", savingsNum: 0 };
    }
    const finalInr = Math.max(plan.inr - appliedCoupon.discount, 1);
    const saved = plan.inr - finalInr;
    return {
      hasDiscount: saved > 0,
      original: `₹${plan.inr}`,
      discounted: `₹${finalInr}`,
      savings: `₹${saved}`,
      savingsNum: saved,
    };
  };

  const validateCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      setCouponError("Please enter a coupon code");
      return;
    }
    setCouponLoading(true);
    setCouponError("");
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code }, { withCredentials: true });
      setAppliedCoupon(res.data);
      setCouponError("");
      toast.success(`Coupon applied! ₹${res.data.discount} off`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid Coupon Code";
      setCouponError(msg);
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  const handlePurchase = async (planId) => {
    if (!user) {
      toast.error("Please login first");
      navigate("/login");
      return;
    }

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway");
        return;
      }

      const res = await axios.post(`${API}/subscription/purchase`, {
        plan_id: planId,
        coupon_code: appliedCoupon?.code || ""
      }, { withCredentials: true });
      const { order_id, amount, currency: orderCurrency, razorpay_key, plan_name } = res.data;

      const options = {
        key: razorpay_key,
        amount: amount,
        currency: orderCurrency,
        name: "Daremusics",
        description: `${plan_name} Plan Subscription`,
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyRes = await axios.post(`${API}/subscription/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, { withCredentials: true });

            if (verifyRes.data.success) {
              await refreshUser();
              sessionStorage.setItem("daremusics_just_paid", plan_name || "your plan");
              // GA4 standard ecommerce 'purchase' event — feeds Funnel Explorer + Revenue reports
              trackEvent(Events.PURCHASE_COMPLETED, {
                transaction_id: response.razorpay_payment_id,
                value: amount / 100,
                currency: orderCurrency,
                items: [{ item_id: planId, item_name: plan_name, price: amount / 100, quantity: 1 }],
              });
              toast.success("Subscription activated! 2500 credits added.");
              navigate("/");
            } else {
              trackEvent(Events.PURCHASE_FAILED, { plan_id: planId, reason: "verify_failed" });
              toast.error("Payment verification failed");
            }
          } catch {
            trackEvent(Events.PURCHASE_FAILED, { plan_id: planId, reason: "verify_exception" });
            toast.error("Payment verification failed. Contact support.");
          }
        },
        prefill: {
          name: user.name || "",
          email: user.email || "",
          contact: user.phone || ""
        },
        theme: { color: "#F59E0B" },
        modal: {
          ondismiss: function () {
            toast("Payment cancelled");
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      trackEvent(Events.CHECKOUT_OPENED, { plan_id: planId, plan_name, amount, currency: orderCurrency });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start purchase");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0C" }}>
      <header className="relative z-10 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="text-white/60 hover:text-white transition-colors" data-testid="pricing-back-btn">
                <ArrowLeft size={24} />
              </button>
              <div className="flex items-center gap-3">
                <Waveform size={28} weight="bold" style={{ color: "#F59E0B" }} />
                <span className="text-xl font-light tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Daremusics</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-8 lg:px-12 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl tracking-tight font-light text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Simple Pricing
          </h2>
          <p className="text-base" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
            One simple plan. 15 credits per song, 2 songs per generation.
          </p>
        </div>

        <div className="grid grid-cols-1 max-w-md mx-auto gap-6">
          {plans.map((plan) => {
            const pb = getPriceBreakdown(plan);
            return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`bg-[#141417] border rounded-2xl p-6 md:p-8 relative transition-all duration-300 hover:-translate-y-1 ${plan.popular ? "border-[#F59E0B]/40 shadow-[0_0_30px_rgba(245,158,11,0.1)]" : "border-white/5 hover:border-white/15"}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F59E0B] text-black text-xs font-bold px-4 py-1 rounded-full">
                  POPULAR
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-xl text-white font-medium mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{plan.name}</h3>
                {pb.hasDiscount ? (
                  <>
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-xl font-light line-through text-white/40" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`plan-original-price-${plan.id}`}>{pb.original}</span>
                      <span className="text-4xl font-light text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`plan-discounted-price-${plan.id}`}>{pb.discounted}</span>
                      <span className="text-sm" style={{ color: "#A1A1AA" }}>/month</span>
                    </div>
                    <p className="text-xs mt-2 font-semibold" style={{ color: "#10B981" }} data-testid={`plan-savings-${plan.id}`}>You saved {pb.savings}!</p>
                  </>
                ) : (
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-light text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid={`plan-price-${plan.id}`}>{pb.original}</span>
                    <span className="text-sm" style={{ color: "#A1A1AA" }}>/month</span>
                  </div>
                )}
                <p className="text-sm mt-2" style={{ color: "#F59E0B" }}>{plan.credits} credits · Unlimited downloads</p>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
                    <CheckCircle size={16} weight="fill" style={{ color: "#10B981" }} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`purchase-${plan.id}-btn`}
                onClick={() => handlePurchase(plan.id)}
                className="w-full font-semibold rounded-full px-8 py-3 transition-all duration-300 hover:-translate-y-0.5 bg-[#F59E0B] text-black hover:bg-[#D97706] shadow-[0_0_20px_rgba(245,158,11,0.2)]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Get Started
              </button>
            </div>
            );
          })}
        </div>

        {/* Coupon Code */}
        {user && (
          <div className="mt-10 max-w-md mx-auto">
            <div className="bg-[#141417] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <Tag size={18} style={{ color: "#F59E0B" }} />
                <span className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: "#F59E0B" }}>Have a Coupon?</span>
              </div>
              {appliedCoupon ? (
                <div className="flex items-center justify-between gap-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl px-4 py-3" data-testid="applied-coupon-display">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle size={18} weight="fill" style={{ color: "#10B981" }} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{appliedCoupon.code} applied</p>
                      <p className="text-xs" style={{ color: "#10B981" }}>₹{appliedCoupon.discount} off — prices updated above</p>
                    </div>
                  </div>
                  <button onClick={removeCoupon} className="text-white/60 hover:text-red-400 transition-colors shrink-0" data-testid="remove-coupon-btn" aria-label="Remove coupon">
                    <X size={18} weight="bold" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      data-testid="coupon-input"
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); if (couponError) setCouponError(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") validateCoupon(); }}
                      placeholder="Enter coupon code"
                      className={`flex-1 bg-[#0A0A0C] border rounded-xl px-4 py-2.5 text-white placeholder:text-gray-500 outline-none text-sm uppercase transition-colors ${couponError ? "border-red-500/50" : "border-white/10"}`}
                    />
                    <button
                      onClick={validateCoupon}
                      disabled={couponLoading}
                      className="bg-[#F59E0B] text-black font-semibold rounded-xl px-5 py-2.5 text-sm hover:bg-[#D97706] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? "..." : "Apply"}
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-sm mt-2 flex items-center gap-1.5" style={{ color: "#EF4444" }} data-testid="coupon-error-msg">
                      <WarningCircle size={14} weight="fill" /> {couponError}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Support Section */}
        <div className="mt-16 text-center">
          <div className="bg-[#141417] border border-white/5 rounded-2xl p-8 inline-block">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Phone size={20} weight="bold" style={{ color: "#F59E0B" }} />
              <span className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: "#F59E0B" }}>Customer Support</span>
            </div>
            <div className="flex gap-6">
              <a href="tel:9493926054" className="text-white hover:text-[#F59E0B] transition-colors" style={{ fontFamily: "'Manrope', sans-serif" }}>9493926054</a>
              <a href="tel:9493924588" className="text-white hover:text-[#F59E0B] transition-colors" style={{ fontFamily: "'Manrope', sans-serif" }}>9493924588</a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
